import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLayerGroup,
  faUsers,
  faFileAlt,
  faUserTie,
  faMoneyBill,
} from "@fortawesome/free-solid-svg-icons";
import "./CreatePlanModal.css";
import { CompanyService } from "Services/CompanyService";
import { toast } from "react-toastify";

interface Props {
  show: boolean;
  handleClose: () => void;
  refreshPlans: () => void;
}

const CreatePlanModal: React.FC<Props> = ({
  show,
  handleClose,
  refreshPlans,
}) => {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    billing: "Monthly",
    max_candidates: "",
    max_exams: "",
    max_proctors: "",
    features: {
      face_detection: false,
      screen_recording: false,
      live_proctoring: false,
      auto_flagging: false,
      id_verification: false,
      report_generation: false,
    },
  });

  const handleChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        [feature]:
          !formData.features[feature as keyof typeof formData.features],
      },
    });
  };

  const validateForm = () => {
    if (!formData.name) {
      toast.error("Plan name is required");
      return false;
    }

    if (!formData.price) {
      toast.error("Price is required");
      return false;
    }

    if (!formData.max_candidates) {
      toast.error("Max candidates required");
      return false;
    }

    if (!formData.max_exams) {
      toast.error("Max exams required");
      return false;
    }

    if (!formData.max_proctors) {
      toast.error("Max proctors required");
      return false;
    }

    return true;
  };

  const createPlan = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        slug: formData.name.toLowerCase().replace(/\s/g, "-"),
        price: Number(formData.price),
        currency: "INR",
        billingCycle: formData.billing.toLowerCase(),
        isFree: Number(formData.price) === 0,

        limits: [
          {
            key: "users",
            value: Number(formData.max_candidates),
          },
          {
            key: "exams",
            value: Number(formData.max_exams),
          },
          {
            key: "proctors",
            value: Number(formData.max_proctors),
          },
          {
            key: "api_calls",
            value: -1,
          },
        ],

        features: Object.keys(formData.features).filter(
          (feature) =>
            formData.features[feature as keyof typeof formData.features],
        ),
      };

      const res = await CompanyService.createPlan(payload);

      if (res.status === 200 || res.status === 201) {
        toast.success("Plan created successfully");

        refreshPlans(); // 🔥 refresh table
        handleClose(); // close modal

        setFormData({
          name: "",
          price: "",
          billing: "Monthly",
          max_candidates: "",
          max_exams: "",
          max_proctors: "",
          features: {
            face_detection: false,
            screen_recording: false,
            live_proctoring: false,
            auto_flagging: false,
            id_verification: false,
            report_generation: false,
          },
        });
      }
    } catch (error) {
      toast.error("Failed to create plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Body className="create-plan-modal">
        <div className="modal-header-custom mb-4">
          <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
          <h5 className="mb-0">Create Subscription Plan</h5>
        </div>

        {/* BASIC INFO */}

        <Card className="mb-4 modal-section-card">
          <Card.Body>
            <h6 className="section-title mb-3">Basic Information</h6>

            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Plan Name</Form.Label>
                  <Form.Control
                    name="name"
                    placeholder="Enter plan name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Price (₹)</Form.Label>
                  <Form.Control
                    name="price"
                    type="number"
                    value={formData.price}
                    placeholder="0"
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Billing Cycle</Form.Label>
                  <Form.Select
                    name="billing"
                    value={formData.billing}
                    onChange={handleChange}
                  >
                    <option>Monthly</option>
                    <option>Yearly</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* LIMITS */}

        <Card className="mb-4 modal-section-card">
          <Card.Body>
            <h6 className="section-title mb-3">Limits</h6>

            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Max Candidates
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="max_candidates"
                    value={formData.max_candidates}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    Max Exams
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="max_exams"
                    value={formData.max_exams}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    <FontAwesomeIcon icon={faUserTie} className="me-2" />
                    Max Proctors
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="max_proctors"
                    value={formData.max_proctors}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* FEATURES */}

        <Card className="mb-4 modal-section-card">
          <Card.Body>
            <h6 className="section-title mb-3">AI Features</h6>

            <div className="feature-grid">
              {Object.keys(formData.features).map((feature, index) => (
                <Badge
                  key={index}
                  onClick={() => handleFeatureToggle(feature)}
                  className={`feature-badge ${
                    formData.features[feature as keyof typeof formData.features]
                      ? "active-feature"
                      : ""
                  }`}
                >
                  {feature.replace("_", " ")}
                </Badge>
              ))}
            </div>
          </Card.Body>
        </Card>

        {/* FOOTER */}

        <div className="text-end">
          <Button variant="light" className="me-2" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            className="save-plan-btn"
            onClick={createPlan}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  animation="border"
                  variant="primary"
                  size="sm"
                  className="me-2"
                />
                Creating Plan...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faMoneyBill} className="me-2" />
                Create Plan
              </>
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreatePlanModal;
